<script>
  import { page } from '$app/stores';
  import { APP_CONFIG } from '$lib/config';
  import {
    closeOverlayModal,
    overlayModal,
    showOverlayModal,
  } from '$lib/features/app/modals';
  import { resetSessionEndingWarning } from '$lib/utils/api';
  import {
    acknowledgeSessionWarning,
    formatTimeLeft,
    getSessionPhase,
    getSessionTimeLeftMs,
    getTokenExpiryTimeMs,
    isSessionWarningAcknowledged,
    readSessionExpiresAt,
    readStoredToken,
  } from '$lib/utils/session';
  import { onMount } from 'svelte';
  import SessionExpiringModal from './SessionExpiringModal.svelte';

  const WARN_BEFORE_MS = APP_CONFIG.SESSION_WARNING_MINUTES * 60_000;
  const CHECK_INTERVAL_MS = 30_000;

  let phase = $state('active');
  // ending: time to the end of the session. expired: time to the logout.
  let timeLeftMs = $state(null);
  let lastExpiresAt = null;

  const isOwnModalOpen = () =>
    $overlayModal?.modalComponent === SessionExpiringModal;

  const closeOwnModal = () => {
    if (isOwnModalOpen()) closeOverlayModal();
  };

  const modalProps = (expiresAt) => ({
    timeLeftMs,
    expired: phase === 'expired',
    onAcknowledge: () => acknowledgeSessionWarning(localStorage, expiresAt),
    onExtend: () => {
      acknowledgeSessionWarning(localStorage, expiresAt);
      // NOTE: Opened from the click handler so popup blockers allow it.
      window.open('/reauth', '_blank');
    },
  });

  const openModal = (expiresAt) =>
    showOverlayModal(SessionExpiringModal, modalProps(expiresAt));

  const check = () => {
    const expiresAt = readSessionExpiresAt(localStorage);
    const now = Date.now();
    const sessionTimeLeftMs = getSessionTimeLeftMs(expiresAt, now);
    const tokenExpiryMs = getTokenExpiryTimeMs(readStoredToken(localStorage));
    const tokenTimeLeftMs = tokenExpiryMs === null ? null : tokenExpiryMs - now;

    // NOTE: The re-authentication page is where the nurse extends the session, warning there is noise.
    if ($page.url.pathname === '/reauth') {
      phase = 'active';
      closeOwnModal();
      return;
    }

    const wasExtended =
      lastExpiresAt &&
      expiresAt &&
      new Date(expiresAt) > new Date(lastExpiresAt);
    if (wasExtended) {
      closeOwnModal();
      resetSessionEndingWarning();
    }
    lastExpiresAt = expiresAt;

    phase = getSessionPhase({
      sessionTimeLeftMs,
      tokenTimeLeftMs,
      warnBeforeMs: WARN_BEFORE_MS,
    });
    timeLeftMs = phase === 'expired' ? tokenTimeLeftMs : sessionTimeLeftMs;

    if (phase !== 'ending' && phase !== 'expired') {
      closeOwnModal();
      return;
    }

    // Keeps an open modal's countdown and wording current.
    if (isOwnModalOpen()) {
      showOverlayModal(SessionExpiringModal, modalProps(expiresAt));
      return;
    }

    // NOTE: Only the warning before the cap opens the modal by itself. Once the session is over a popup could interrupt typing right before the logout, the red label (and the toast) already say it.
    if (phase !== 'ending') return;
    // NOTE: Never replace another overlay (e.g. a confirmation the nurse is answering).
    if ($overlayModal) return;
    if (isSessionWarningAcknowledged(localStorage, expiresAt)) return;
    openModal(expiresAt);
  };

  onMount(() => {
    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  });

  const showLabel = $derived(
    (phase === 'ending' || phase === 'expired') &&
      $page.url.pathname !== '/reauth',
  );

  const reopenModal = () => {
    const expiresAt = readSessionExpiresAt(localStorage);
    if (expiresAt && !$overlayModal) openModal(expiresAt);
  };
</script>

{#if showLabel}
  <button
    class="fixed bottom-0 left-1/2 z-[900] -translate-x-1/2 rounded-t px-3 py-1 text-sm shadow {phase ===
    'expired'
      ? 'bg-error text-red-800'
      : 'bg-yellow text-yellow-800'}"
    onclick={reopenModal}
  >
    {#if phase === 'expired'}
      Du loggas ut om {formatTimeLeft(timeLeftMs)}. Förläng nu
    {:else}
      Session går ut om {formatTimeLeft(timeLeftMs)}
    {/if}
  </button>
{/if}
